import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import PayoLogo from './PayoLogo';
import { getActivityRow } from '../lib/activityFormat';
import { 
  Plus, 
  Users, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Send,
  Settings,
  LogOut,
  ChevronRight,
  DollarSign,
  CreditCard,
  RefreshCw,
  Shield,
  Download,
  ChevronDown,
  Zap,
  Eye
} from 'lucide-react';
import { mockUser } from '../mock/mockData';
import { useAuth } from '../context/CompleteAuthContext';
import PaymentModal from './PaymentModal';
import { toast } from 'sonner';

const Dashboard = ({ onAddExpense, onCreateGroup, onViewGroup, onManageSubscriptions, onSecuritySettings }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [groups, setGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [quickSplits, setQuickSplits] = useState([]);
  const [isLoadingQS, setIsLoadingQS] = useState(true);
  const [walletBalanceCents, setWalletBalanceCents] = useState(null);
  const [walletTxns, setWalletTxns] = useState([]);

  const unreadNotifications = []; // Notifications disabled for launch

  // Get display name with proper fallback logic
  const getDisplayName = () => {
    if (!user) return 'User';
    
    // Priority 1: full_name from profile
    if (user.full_name && user.full_name.trim()) {
      return user.full_name.trim();
    }
    
    // Priority 2: name from OAuth (Google, etc.)
    if (user.name && user.name.trim()) {
      return user.name.trim();
    }
    
    // Priority 3: email prefix (before @)
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    return 'User';
  };

  const displayName = getDisplayName();
  const userInitial = (() => {
    const name = displayName || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.charAt(0).toUpperCase() || '?';
  })();

  // Calculate group IOU balance (separate from wallet)
  const calculateGroupBalance = () => {
    let total = 0;
    if (groups && groups.length > 0) {
      groups.forEach(group => {
        if (group.members && Array.isArray(group.members)) {
          const currentUserMember = group.members.find(
            m => m.user_id === user?.id || m.email === user?.email
          );
          if (currentUserMember && typeof currentUserMember.balance === 'number') {
            total += currentUserMember.balance;
          }
        }
      });
    }
    return total;
  };

  const groupBalance = calculateGroupBalance();
  const walletDollars = (walletBalanceCents || 0) / 100;
  const walletLoaded = walletBalanceCents !== null;

  // Fetch user's groups
  useEffect(() => {
    const fetchGroups = async () => {
      if (!user) return;
      
      try {
        const BACKEND_URL = API_URL;
        const response = await fetch(`${BACKEND_URL}/api/groups`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setGroups(data);
        }
        // On error (429, 500, etc), keep existing groups data — don't wipe to []
      } catch (error) {
        console.error('Error fetching groups:', error);
        // Keep existing state — don't reset to []
      } finally {
        setIsLoadingGroups(false);
      }
    };
    
    fetchGroups();
  }, [user]);

  // Fetch host's quick split sessions + wallet balance
  useEffect(() => {
    const fetchQuickSplits = async () => {
      if (!user) { setIsLoadingQS(false); return; }
      try {
        const BACKEND_URL = API_URL;
        const [sessRes, walletRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/quick-split/my-sessions`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/wallet`, { credentials: 'include' }),
        ]);
        if (sessRes.ok) setQuickSplits(await sessRes.json());
        if (walletRes.ok) {
          const w = await walletRes.json();
          setWalletBalanceCents(w.balance_cents || 0);
          setWalletTxns((w.transactions || []).slice(0, 5));
        }
      } catch { /* silent */ }
      setIsLoadingQS(false);
    };
    fetchQuickSplits();
  }, [user]);

  // Calculate debts for payment
  const debts = groups
    .filter(group => group.balance < 0)
    .map(group => ({
      groupId: group.id,
      groupName: group.name,
      amount: Math.abs(group.balance),
      // In real app, this would be the actual person you owe
      owedTo: group.members.find(m => m.id !== mockUser.id)?.name || 'Group members'
    }));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-green-600';
    if (balance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getBalanceIcon = (balance) => {
    if (balance > 0) return <TrendingUp className="w-4 h-4" />;
    if (balance < 0) return <TrendingDown className="w-4 h-4" />;
    return <DollarSign className="w-4 h-4" />;
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleSendPayment = (debt) => {
    setSelectedPayment(debt);
    setShowPaymentModal(true);
  };

  // Check for payment success on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const sessionId = urlParams.get('session_id');
    
    if (paymentSuccess && sessionId) {
      // Clear URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Show success message
      const amount = sessionStorage.getItem('payment_amount');
      const recipient = sessionStorage.getItem('payment_recipient');
      
      if (amount && recipient) {
        toast.success('Payment completed successfully!', {
          description: `$${amount} sent to ${recipient}`
        });
        
        // Clear session storage
        sessionStorage.removeItem('pending_session_id');
        sessionStorage.removeItem('payment_amount');
        sessionStorage.removeItem('payment_recipient');
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 h-16">
        <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/')} className="flex items-center hover:opacity-80 transition-opacity" data-testid="payo-home-link">
              <PayoLogo size="md" />
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Avatar className="w-8 h-8">
                {user?.picture ? (
                  <AvatarImage src={user.picture} alt={displayName} />
                ) : null}
                <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold text-sm">{userInitial}</AvatarFallback>
              </Avatar>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-900">{displayName}</p>
                <p className="text-xs text-gray-500">Welcome back</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="py-2 px-2.5 cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings?tab=security')} className="py-2 px-2.5 cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" />
                    Security
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings?tab=billing')} className="py-2 px-2.5 cursor-pointer">
                    <Download className="w-4 h-4 mr-2" />
                    Billing & Data
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/bank-accounts')} className="py-2 px-2.5 cursor-pointer">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Bank Accounts
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="py-2 px-2.5 cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Activity — unified money movement feed (full-width) */}
        <div className="mb-8">
          {/* Activity — recent transaction feed (no balance concept) */}
          <Card data-testid="dashboard-activity-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Activity</CardTitle>
                <Button variant="link" size="sm" className="px-0 text-xs text-blue-600 h-auto" onClick={() => navigate('/wallet')} data-testid="dashboard-view-all-activity">
                  View all transactions →
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!walletLoaded ? (
                <div className="animate-pulse space-y-2">
                  <div className="h-4 w-full bg-gray-100 rounded"></div>
                  <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
                </div>
              ) : walletTxns.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4" data-testid="dashboard-activity-empty">No activity yet</p>
              ) : (
                <div className="space-y-1">
                  {walletTxns.slice(0, 4).map((txn, i) => {
                    const { label, tag, typeTag, isIncoming: isIn } = getActivityRow(txn);
                    // Status tags (Paid / Pending / External) keep colored emphasis.
                    // Type tags (Quick Split) read as subtle metadata, not a badge.
                    return (
                      <div key={txn.id || i} className="flex items-center justify-between py-1.5 sm:py-2" data-testid={`dashboard-activity-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
                            {tag && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tag.cls}`}>{tag.text}</span>}
                            {typeTag && <span className="text-[10px] uppercase tracking-wide text-gray-400" data-testid={`dashboard-activity-type-${i}`}>· {typeTag}</span>}
                          </div>
                          <p className="text-xs text-gray-400">{timeAgo(txn.created_at)}</p>
                        </div>
                        <span className={`text-sm font-bold ml-3 ${isIn ? 'text-green-600' : 'text-gray-700'}`}>
                          {isIn ? '+' : '-'}${(Math.abs(txn.amount_cents) / 100).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending Payments — hidden per dashboard cleanup; access via Group page instead */}

        {/* Main Content */}
        <div className="space-y-6">
          <div className="space-y-6">
            {/* Quick Splits */}
            {!isLoadingQS && quickSplits.length > 0 && (() => {
              const currentSplits = quickSplits.filter(qs => qs.status === 'active');
              const pastSplits = quickSplits.filter(qs => qs.status !== 'active');

              return (
                <Card data-testid="qs-dashboard-section">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold flex items-center">
                        <Zap className="w-5 h-5 mr-2 text-blue-600" />
                        Quick Splits
                      </CardTitle>
                      <Button size="sm" variant="outline" onClick={() => navigate('/quick-split/create')} data-testid="qs-dashboard-new-btn">
                        <Plus className="w-4 h-4 mr-1" /> New
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Current */}
                    {currentSplits.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Current</p>
                        <div className="divide-y divide-gray-100">
                          {currentSplits.slice(0, 3).map(qs => {
                            const collectible = qs.collectible_count ?? Math.max(0, (qs.number_of_people || 1) - 1);
                            const collectingCents = (qs.per_person_amount_cents || 0) * collectible;
                            const remaining = Math.max(0, collectible - (qs.paid_count || 0));
                            const isFullyCollected = collectible > 0 && remaining === 0;
                            return (
                              <div
                                key={qs.id}
                                className="flex items-center gap-3 py-2.5 sm:py-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                                onClick={() => navigate(`/quick-split/${qs.id}/host`)}
                                data-testid={`qs-dashboard-item-${qs.id}`}
                              >
                                <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                                  <DollarSign className="w-4 h-4 text-green-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">{qs.description || 'Quick Split'}</p>
                                  <p
                                    className="text-xs text-gray-500 mt-0.5"
                                    data-testid={`qs-dashboard-progress-${qs.id}`}
                                  >
                                    {isFullyCollected
                                      ? `Fully collected · ${qs.paid_count}/${collectible}`
                                      : remaining === 1
                                      ? 'Waiting on 1 person'
                                      : `Waiting on ${remaining} people`}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p
                                    className="text-sm font-semibold text-green-700 tabular-nums"
                                    data-testid={`qs-dashboard-collecting-${qs.id}`}
                                  >
                                    {formatCurrency(collectingCents / 100)}
                                  </p>
                                  <p
                                    className="text-[11px] text-gray-400 tabular-nums"
                                    data-testid={`qs-dashboard-total-${qs.id}`}
                                  >
                                    {formatCurrency(qs.total_amount_cents / 100)} total
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Past (compact) */}
                    {pastSplits.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Past</p>
                        <div className="divide-y divide-gray-100">
                          {pastSplits.slice(0, 2).map(qs => (
                            <div
                              key={qs.id}
                              className="flex items-center gap-3 py-2.5 sm:py-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                              onClick={() => navigate(`/quick-split/${qs.id}/host`)}
                              data-testid={`qs-dashboard-item-${qs.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-600 truncate">{qs.description || 'Quick Split'}</p>
                                <p className="text-[11px] text-gray-400 capitalize mt-0.5">{qs.status}</p>
                              </div>
                              <span className="text-sm text-gray-500 ml-2">{formatCurrency(qs.total_amount_cents / 100)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentSplits.length === 0 && pastSplits.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-2">No Quick Splits yet</p>
                    )}

                    <button onClick={() => navigate('/splits')} className="w-full text-center text-xs text-blue-600 hover:text-blue-800 py-1" data-testid="qs-dashboard-view-all">
                      View all Quick Splits →
                    </button>
                  </CardContent>
                </Card>
              );
            })()}

            {!isLoadingQS && quickSplits.length === 0 && (
              <Card data-testid="qs-dashboard-section">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-blue-600" />
                      Quick Splits
                    </CardTitle>
                    <Button size="sm" variant="outline" onClick={() => navigate('/quick-split/create')} data-testid="qs-dashboard-new-btn">
                      <Plus className="w-4 h-4 mr-1" /> New
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-400 text-center py-3">No Quick Splits yet</p>
                  <button onClick={() => navigate('/splits')} className="w-full text-center text-xs text-blue-600 hover:text-blue-800 py-1" data-testid="qs-dashboard-view-all">
                    View all Quick Splits →
                  </button>
                </CardContent>
              </Card>
            )}

            {/* Groups card hidden for launch (Quick Splits-first) */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">

          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPayment && (
        <PaymentModal 
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPayment(null);
          }}
          payment={selectedPayment}
        />
      )}
    </div>
  );
};

export default Dashboard;